import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Wrench,
  GitCompare,
  ShieldCheck,
  Loader2,
  FileText,
  ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import DocumentComparison from "../components/DocumentComparison";
import ProposalSanityChecker from "../components/ProposalSanityChecker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Tools = () => {
  const [rfps, setRfps] = useState([]);
  const [selectedRfpId, setSelectedRfpId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("comparison");

  useEffect(() => {
    fetchRfps();
  }, []);

  const fetchRfps = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/rfp/list`);
      setRfps(res.data);
      if (res.data.length > 0) {
        setSelectedRfpId(res.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching RFPs:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectedRfp = rfps.find(r => r.id === selectedRfpId);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#007AFF]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="tools-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#0F172A] font-['Manrope'] flex items-center gap-3">
          <Wrench className="w-7 h-7 text-[#007AFF]" />
          RFP Tools
        </h1>
        <p className="text-[#64748B] mt-1">
          Advanced tools for document comparison and proposal validation
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#F1F5F9]">
          <TabsTrigger 
            value="comparison" 
            className="data-[state=active]:bg-white flex items-center gap-2"
          >
            <GitCompare className="w-4 h-4" />
            Document Comparison
          </TabsTrigger>
          <TabsTrigger 
            value="sanity" 
            className="data-[state=active]:bg-white flex items-center gap-2"
          >
            <ShieldCheck className="w-4 h-4" />
            Sanity Checker
          </TabsTrigger>
        </TabsList>

        {/* Document Comparison Tab */}
        <TabsContent value="comparison" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <DocumentComparison />
            </div>
            <div>
              <Card className="border-[#E2E8F0]">
                <CardHeader>
                  <CardTitle className="text-base font-bold">How It Works</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#007AFF] text-white flex items-center justify-center text-xs font-bold">
                      1
                    </div>
                    <div>
                      <p className="font-medium text-sm">Select RFPs</p>
                      <p className="text-xs text-[#64748B]">Choose 2-3 RFPs to compare</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#007AFF] text-white flex items-center justify-center text-xs font-bold">
                      2
                    </div>
                    <div>
                      <p className="font-medium text-sm">Run Comparison</p>
                      <p className="text-xs text-[#64748B]">System analyzes sections and requirements</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#007AFF] text-white flex items-center justify-center text-xs font-bold">
                      3
                    </div>
                    <div>
                      <p className="font-medium text-sm">Review Results</p>
                      <p className="text-xs text-[#64748B]">See similarities and differences</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Sanity Checker Tab */}
        <TabsContent value="sanity" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              {/* RFP Selector */}
              <Card className="border-[#E2E8F0] mb-6">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-bold">Select RFP to Validate</CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={selectedRfpId} onValueChange={setSelectedRfpId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select an RFP" />
                    </SelectTrigger>
                    <SelectContent>
                      {rfps.map(rfp => (
                        <SelectItem key={rfp.id} value={rfp.id}>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            <span className="truncate">{rfp.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {/* Sanity Checker Component */}
              {selectedRfpId && (
                <ProposalSanityChecker 
                  rfpId={selectedRfpId} 
                  rfpName={selectedRfp?.name}
                />
              )}
            </div>

            <div>
              <Card className="border-[#E2E8F0]">
                <CardHeader>
                  <CardTitle className="text-base font-bold">What Gets Checked</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-[#007AFF] mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Compliance Items</p>
                      <p className="text-xs text-[#64748B]">All mandatory items addressed</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-[#007AFF] mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Section Coverage</p>
                      <p className="text-xs text-[#64748B]">All sections have summaries</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-[#007AFF] mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Company Profile</p>
                      <p className="text-xs text-[#64748B]">All required fields filled</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 text-[#007AFF] mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">Important Dates</p>
                      <p className="text-xs text-[#64748B]">Deadlines identified</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Tools;
